// @flow

// --------------
// | Disclaimer |
// --------------
// You'll find several comments about legacy in the following code and I don't
// found any good explanations. This was ported from several files like
// - `src/CertificateCreate/index.js` (handleSubmit)
// - `src/CertificateCreate/modules/selectors.js` (selectCertificateToRenewOptions)
// And after refactor it still does not make sense. I try to keep as iso as
// possible.

import pick from 'lodash/pick';

import type { ProductData } from '@gandi/cart';
import {
  computeCertificateCatalogType,
  retrieveCertificateConfiguration,
} from '@gandi/redux-resource-price';
import type {
  CertificatePrice,
  ProductPricesList,
  SelectCertificateCatalogTypeArgs,
} from '@gandi/redux-resource-price';

import type {
  CertificateApiResponse,
  CertificateProductPayload,
  FormValues,
  Phase,
} from '../types';

export const hasApexOnlyField = (formValues: FormValues): boolean => {
  return (
    formValues.provider === 'digicert' &&
    formValues.type === 'standard' &&
    formValues.protect === 'single'
  );
};

const findCertificatePriceDetails = (
  certificateProductList: void | ProductPricesList<CertificatePrice, Phase>,
  packageName: string,
) => {
  return certificateProductList?.products
    ? certificateProductList.products.find(
        product => product.name === packageName,
      )
    : undefined;
};

const computeProductDataPayload = (
  possibilies: void | ProductPricesList<CertificatePrice, Phase>,
  packageName: string,
  formValues: FormValues,
  migrationUuid?: string,
): CertificateProductPayload => {
  const certificateDetails = findCertificatePriceDetails(
    possibilies,
    packageName,
  );
  const { cn, dcvMethod } = formValues;

  const payload: CertificateProductPayload = {
    package: packageName,
    cn,
    duration: 1,
    dcv_method: dcvMethod,
    options: (formValues: any), // extra for overview display
    // $FlowFixMe[incompatible-type]
    certificateDetails,
  };

  if (formValues.csr) {
    // custom, otherwhise auto for shs
    payload.csr = formValues.csr;
  }

  if (hasApexOnlyField(formValues)) {
    // In case the user uncheck the box `apex_and_www` we will send `apex_only=true`
    // By default cert is created on both apex AND www
    // Note: shs is not concerned by this prop
    payload.apex_only = !formValues.apex_and_www;
  }

  // add altnames to payload
  if (Array.isArray(formValues.altnames)) {
    const filteredAltnames = formValues.altnames.filter(Boolean);
    if (filteredAltnames.length) {
      payload.altnames = filteredAltnames;
    }
  }

  if (migrationUuid) {
    // in case of renew and the provider has been changed (cf. CertificateProductPayload)
    payload.migration_uuid = migrationUuid;
  }

  return payload;
};

export const mapFormValuesCreateToProductData = (
  formValues: FormValues,
  certificateCreatePossibilities: void | ProductPricesList<
    CertificatePrice,
    Phase,
  >,
): ProductData => {
  const catalogTypeArg: SelectCertificateCatalogTypeArgs = pick(formValues, [
    'provider',
    'type',
    'protect',
    'numberAddresses',
    'warranty',
  ]);

  const packageName = computeCertificateCatalogType(catalogTypeArg);

  const payload = computeProductDataPayload(
    certificateCreatePossibilities,
    packageName || '',
    formValues,
  );

  const productData: ProductData = {
    product_type: 'certificate',
    process: 'create',
    // TODO improve @gandi/cart ProductPayload -> Make payload contravariant: `-payload: ProductPayload`
    // $FlowFixMe[prop-missing]
    payload,
  };
  // ---

  return productData;
};

/**
 * This is very similar to the create mapper except we pass the migrationUuid to the payload
 */
export const mapFormValuesMigrateToProductData = (
  formValues: FormValues,
  certificateCreatePossibilities: void | ProductPricesList<
    CertificatePrice,
    Phase,
  >,
): ProductData => {
  const catalogTypeArg: SelectCertificateCatalogTypeArgs = pick(formValues, [
    'provider',
    'type',
    'protect',
    'numberAddresses',
    'warranty',
  ]);

  const packageName = computeCertificateCatalogType(catalogTypeArg);

  const payload = computeProductDataPayload(
    certificateCreatePossibilities,
    packageName || '',
    formValues,
    formValues.uuid,
  );

  const productData: ProductData = {
    product_type: 'certificate',
    process: 'create',
    // $FlowFixMe[prop-missing]
    payload,
  };
  // ---

  return productData;
};

const computeCatalogName = (certificate: CertificateApiResponse): string =>
  // $FlowIgnore[incompatible-return] split never returns an empty array, so pop never returns undefined
  certificate.package.href.split('/').pop();

export const renewableCertificateToFormValues = (
  certificate: CertificateApiResponse,
): FormValues => {
  const catalogName = computeCatalogName(certificate);
  // There's a small chance but should not happend that catalogName is empty or not correctly set
  // Default values will be given in that situation but might not be expected
  const {
    type = 'standard',
    protect = 'single',
    numberAddresses,
    warranty,
    provider = 'digicert',
  } = retrieveCertificateConfiguration(catalogName) || {};

  const uuid = certificate.uuid;

  const { cn, altnames } = certificate;

  return {
    /**
     * uncheck by default, it will be sent only if it's digicert/standard/single
     */
    apex_and_www: false,
    provider,
    uuid,
    type,
    protect,
    numberAddresses,
    warranty,
    cn,
    altnames,
  };
};

/**
 * Return the payload to add a certificate in the cart.
 * Used also to validate the product before adding to the cart.
 */
export const mapFormValuesRenewToProductData = (
  formValues: FormValues,
  certificate: CertificateApiResponse,
  certificateRenewPossibilities: void | ProductPricesList<
    CertificatePrice,
    Phase,
  >,
): ProductData => {
  const catalogTypeArg: SelectCertificateCatalogTypeArgs = pick(formValues, [
    'provider',
    'type',
    'protect',
    'numberAddresses',
    'warranty',
  ]);
  // We force to recompute packagename based on formValues (the warranty for sectigo/pro may have changed)
  const packageName = computeCertificateCatalogType(catalogTypeArg);

  const payload = computeProductDataPayload(
    certificateRenewPossibilities,
    packageName || '',
    formValues,
  );

  const productData: ProductData = {
    product_type: 'certificate',
    process: 'renew',
    // TODO improve @gandi/cart ProductPayload -> Make payload contravariant: `-payload: ProductPayload`
    // $FlowFixMe[prop-missing]
    payload,
  };
  // ---

  productData.identifier = certificate.uuid;

  return productData;
};
